import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Input,
  Space,
  message,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import InvoiceTable from "./InvoiceTable";
import axiosInstance from "../../configs";
import { canEdit, MODULES } from "../../utils/rbac";

const { Title, Text } = Typography;

export default function Jasa() {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortDirection] = useState("DESC");
  const [sortField] = useState("invoice_date");

  const fetchTableData = useCallback((pageNumber = page, pageSize = rowsPerPage) => {
    setLoading(true);
    setDataTable([]); // Clear data lama sebelum fetch baru
    axiosInstance
      .get("/finance/sales-invoices", {
        params: {
          page: pageNumber + 1,
          size: pageSize,
          search: committedSearch,
          startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
          endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
          sortDirection,
          sortField,
        },
      })
      .then((response) => {
        const responseData = response.data?.data || {};
        const LABELS = { 1: 'Handling', 2: 'Storage', 3: 'Cukai' };
        const content = (responseData?.content || []).map((item) => ({
          id: item.id_invoice,
          idItem: item.id_invoice,
          invoiceNumber: item.invoice_number,
          invoiceDate: item.invoice_date,
          documentName: `BC. ${item.document_type || '16'} - Invoice ${LABELS[item.type] || ''}`,
          documentNumber: item.nopen,
          documentDate: item.document_date || item.created_at,
          status: item.status,
          periodEnd: item.due_date,
          ...item,
        }));
        setDataTable(content);
        setTotalData(responseData?.totalElements || 0);
      })
      .catch((error) => {
        setError("Failed to fetch data.");
        message.error("Gagal mengambil data invoice");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, rowsPerPage, committedSearch, startDate, endDate, sortDirection, sortField]);

  useEffect(() => {
    fetchTableData(page, rowsPerPage);
  }, [fetchTableData, page, rowsPerPage]);

  const handleSearch = () => {
    setCommittedSearch(search);
    setPage(0);
    // fetchTableData akan trigger otomatis lewat useEffect jika dependency (page/committedSearch) berubah
  };

  const handleResetFilter = () => {
    setSearch("");
    setCommittedSearch("");
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleDownloadExcel = () => {
    const params = new URLSearchParams({
      search,
      startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
      endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
    });
    // Use window.location.href or window.open for direct download
    const url = `${axiosInstance.defaults.baseURL}/finance/export-excel?${params.toString()}`;
    window.open(url, "_blank");
  };

  const handleDelete = (id) => {
    setLoading(true);
    axiosInstance
      .delete(`/finance/sales-invoices/${id}`)
      .then((response) => {
        message.success("Data berhasil dihapus");
        fetchTableData();
      })
      .catch((error) => {
        setLoading(false);
        console.error(
          "Error deleting item:",
          error.response ? error.response.data : error.message
        );
        message.error("Gagal menghapus data");
      });
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }} style={{ marginBottom: 60 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Finance - Sales Invoice
          </Title>
          <Text type="secondary">
            Kelola daftar Sales Invoice untuk kebutuhan finance.
          </Text>
        </Col>
        <Col>
          <Space>
            {canEdit(MODULES.FINANCE) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => history.push("/finance/add")}
                style={{
                  background: "#1BC5BD",
                  borderColor: "#1BC5BD",
                  fontWeight: 600,
                }}
              >
                Tambah
              </Button>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleDownloadExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTERS */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Cari Invoice / Dokumen</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Nomor Invoice / Dokumen..."
            style={{ marginTop: 4 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col xs={24} sm={6} md={4}>
          <Text strong>Tanggal Awal</Text>
          <DatePicker
            style={{ width: "100%", marginTop: 4 }}
            value={startDate}
            onChange={(date) => setStartDate(date)}
            placeholder="Pilih Tanggal"
          />
        </Col>
        <Col xs={24} sm={6} md={4}>
          <Text strong>Tanggal Akhir</Text>
          <DatePicker
            style={{ width: "100%", marginTop: 4 }}
            value={endDate}
            onChange={(date) => setEndDate(date)}
            placeholder="Pilih Tanggal"
          />
        </Col>

        <Col xs={24} md={8} style={{ textAlign: "right", alignSelf: "end" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
      </Row>

      {/* TABLE */}
      <div style={{ marginTop: 24 }}>
        <InvoiceTable
          dataSource={dataTable}
          page={page}
          rowsPerPage={rowsPerPage}
          totalData={totalData}
          loading={loading}
          handleDelete={handleDelete}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(ps) => { setRowsPerPage(ps); setPage(0); }}
        />
      </div>
    </Card>
  );
}
